import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import DataObjectIcon from '@mui/icons-material/DataObject'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import { FC, useCallback, useMemo, useRef, useState } from 'react'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface TagCategory {
  id: string
  label: string
}

export interface Tag {
  id: string
  label: string
  value: string
  categoryId: string
  popular?: boolean
}

export interface TagInsertInputProps {
  /** Current text value */
  value: string
  /** Called when the text value changes (typing or tag insertion) */
  onChange: (value: string) => void
  /** Available tags */
  tags: Tag[]
  /** Categories that group tags */
  categories: TagCategory[]
  /**
   * Converts a tag value into the string that gets inserted.
   * @default (v) => `{{${v}}}`
   */
  formatter?: (tagValue: string) => string
  /** All remaining props are forwarded to the MUI TextField */
  label?: TextFieldProps['label']
  multiline?: boolean
  rows?: number
  minRows?: number
  helperText?: TextFieldProps['helperText']
  fullWidth?: boolean
  size?: TextFieldProps['size']
  sx?: TextFieldProps['sx']
  placeholder?: string
  disabled?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POPOVER_WIDTH = 360
const POPOVER_MAX_HEIGHT = 420

const defaultFormatter = (v: string): string => `{{${v}}}`

// ---------------------------------------------------------------------------
// Tag picker popover (internal)
// ---------------------------------------------------------------------------

interface TagPickerProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  tags: Tag[]
  categories: TagCategory[]
  onSelectTag: (tag: Tag) => void
}

const TagPicker: FC<TagPickerProps> = ({
  anchorEl,
  open,
  onClose,
  tags,
  categories,
  onSelectTag,
}) => {
  const [search, setSearch] = useState('')
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    setSearch('')
    setActiveCategoryId(null)
    onClose()
  }, [onClose])

  const handleSelect = useCallback(
    (tag: Tag) => {
      onSelectTag(tag)
      handleClose()
    },
    [onSelectTag, handleClose],
  )

  const popularTags = useMemo(() => tags.filter((t) => t.popular), [tags])

  const filteredTags = useMemo(() => {
    if (!search) return tags
    const q = search.toLowerCase()
    return tags.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.value.toLowerCase().includes(q),
    )
  }, [tags, search])

  const activeCategoryTags = useMemo(
    () =>
      activeCategoryId
        ? filteredTags.filter((t) => t.categoryId === activeCategoryId)
        : [],
    [filteredTags, activeCategoryId],
  )

  const isSearching = search.length > 0

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            width: POPOVER_WIDTH,
            maxHeight: POPOVER_MAX_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Search field */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Zoeken..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setActiveCategoryId(null)
          }}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />

      {/* Content area */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {/* --- Searching: flat filtered list --- */}
        {isSearching && (
          <Box sx={{ p: 1.5 }}>
            {filteredTags.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                Geen resultaten
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {filteredTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.label}
                    size="small"
                    onClick={() => handleSelect(tag)}
                    clickable
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* --- Category drill-down view --- */}
        {!isSearching && activeCategoryId && (
          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setActiveCategoryId(null)}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="subtitle2">
                {categories.find((c) => c.id === activeCategoryId)?.label}
              </Typography>
            </Box>
            {activeCategoryTags.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                Geen tags in deze categorie
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {activeCategoryTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    label={tag.label}
                    size="small"
                    onClick={() => handleSelect(tag)}
                    clickable
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* --- Default view: popular + categories --- */}
        {!isSearching && !activeCategoryId && (
          <>
            {popularTags.length > 0 && (
              <Box sx={{ p: 1.5, pb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  Populair
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                  {popularTags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.label}
                      size="small"
                      onClick={() => handleSelect(tag)}
                      clickable
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {popularTags.length > 0 && categories.length > 0 && <Divider />}

            {categories.length > 0 && (
              <Box sx={{ pb: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    px: 1.5,
                    pt: 1.5,
                    display: 'block',
                  }}
                >
                  Categorieën
                </Typography>
                <List dense disablePadding>
                  {categories.map((cat) => {
                    const count = tags.filter(
                      (t) => t.categoryId === cat.id,
                    ).length
                    return (
                      <ListItemButton
                        key={cat.id}
                        onClick={() => setActiveCategoryId(cat.id)}
                        sx={{ px: 1.5 }}
                      >
                        <ListItemText
                          primary={cat.label}
                          secondary={`${count} tag${count !== 1 ? 's' : ''}`}
                        />
                      </ListItemButton>
                    )
                  })}
                </List>
              </Box>
            )}
          </>
        )}
      </Box>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TagInsertInput: FC<TagInsertInputProps> = ({
  value,
  onChange,
  tags,
  categories,
  formatter = defaultFormatter,
  label,
  multiline,
  rows,
  minRows,
  helperText,
  fullWidth = true,
  size,
  sx,
  placeholder,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const cursorPosRef = useRef<number>(0)

  const handleOpenPicker = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // Save cursor position before focus moves to the popover
      if (inputRef.current) {
        cursorPosRef.current =
          inputRef.current.selectionStart ?? value.length
      }
      setAnchorEl(event.currentTarget)
    },
    [value.length],
  )

  const handleSelectTag = useCallback(
    (tag: Tag) => {
      const insertion = formatter(tag.value)
      const pos = cursorPosRef.current
      const before = value.slice(0, pos)
      const after = value.slice(pos)
      const newValue = before + insertion + after
      onChange(newValue)

      // Restore cursor after insertion
      requestAnimationFrame(() => {
        if (inputRef.current) {
          const newPos = pos + insertion.length
          inputRef.current.focus()
          inputRef.current.setSelectionRange(newPos, newPos)
        }
      })
    },
    [value, formatter, onChange],
  )

  return (
    <>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
        sx={{
          ...sx,
          ...(multiline && !rows
            ? { '& .MuiInputBase-inputMultiline': { resize: 'vertical' } }
            : {}),
        }}
        placeholder={placeholder}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={multiline ? { alignSelf: 'flex-start', mt: 1 } : undefined}>
              <IconButton
                size="small"
                onClick={handleOpenPicker}
                disabled={disabled}
                title="Tag invoegen"
                edge="start"
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TagPicker
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        tags={tags}
        categories={categories}
        onSelectTag={handleSelectTag}
      />
    </>
  )
}

export default TagInsertInput
